import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

import { TileWidthToggleProvider, useTileErrorBoundary, useTileSlot, useTilingEngine } from './TilingEngineContext';
import type { TileKind } from './types';
import { useTileWidthToggleValue } from './use-tile-width-toggle';

export type TilingEnginePortalProps = {
    readonly autoScrollKey?: string;
    readonly children: ReactNode;
    readonly holdAutoScroll?: boolean;
    readonly id: string;
    readonly kind?: TileKind;
    readonly maxWidth?: string;
    readonly minWidth?: string;
    readonly orderKey?: number;
    readonly pinRight?: boolean;
    readonly resizeKey?: string;
    readonly width?: string;
    readonly zIndex?: number;
};

export const TilingEnginePortal: FunctionComponent<TilingEnginePortalProps> = ({
    autoScrollKey,
    children,
    holdAutoScroll,
    id,
    kind,
    maxWidth,
    minWidth,
    orderKey,
    pinRight,
    resizeKey,
    width,
    zIndex,
}) => {
    const { registerTile, updateTile } = useTilingEngine();
    const slot = useTileSlot(id);
    const ErrorBoundary = useTileErrorBoundary();
    const widthToggle = useTileWidthToggleValue(id, resizeKey);

    // biome-ignore lint/correctness/useExhaustiveDependencies: mutable registration props are synced through updateTile so we don't unregister and reregister on every prop change
    useLayoutEffect(() => {
        return registerTile({
            autoScrollKey,
            holdAutoScroll,
            id,
            kind,
            maxWidth,
            minWidth,
            orderKey,
            pinRight,
            resizeKey,
            width,
            zIndex,
        });
    }, [holdAutoScroll, id, kind, pinRight, registerTile, resizeKey, zIndex]);

    useLayoutEffect(() => {
        updateTile(id, { autoScrollKey, maxWidth, minWidth, orderKey, width });
    }, [autoScrollKey, id, maxWidth, minWidth, orderKey, updateTile, width]);

    if (is.null(slot)) {
        return null;
    }

    const portalChildren = is.nullOrUndefined(ErrorBoundary) ? (
        children
    ) : (
        <ErrorBoundary tileId={id}>{children}</ErrorBoundary>
    );

    return createPortal(<TileWidthToggleProvider value={widthToggle}>{portalChildren}</TileWidthToggleProvider>, slot);
};
